/**
 * Canvas node registry display-name i18n (full registry).
 *
 * `nodeRegistry.tsx` remains the structural SSOT (icons, categories,
 * families, suggestedNext, defaultFields). All user-visible labels and
 * descriptions resolve through semantic keys in the `canvas` namespace:
 * `nodes.<nodeType>.label` / `.description`.
 *
 * Adding a new locale requires only resource files + registry entry —
 * no changes to Canvas node components.
 */
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import type { CanvasNodeCategory } from './types'
import { resolveNodeInstanceLabel, isSystemDefaultInstanceLabel } from './canvasNodeDisplay'

export function canvasNodeLabelKey(nodeType: string): string {
  return `nodes.${nodeType}.label`
}

export function canvasNodeDescriptionKey(nodeType: string): string {
  return `nodes.${nodeType}.description`
}

export function canvasCategoryLabelKey(category: CanvasNodeCategory): string {
  return `categories.${category}`
}

export function useCanvasNodeRegistryI18n() {
  const { t, i18n } = useTranslation('canvas')

  const nodeLabel = useCallback(
    (nodeType: string, fallbackLabel?: string): string => {
      const key = canvasNodeLabelKey(nodeType)
      if (!i18n.exists(key, { ns: 'canvas' })) return fallbackLabel ?? nodeType
      return t(key)
    },
    [i18n, t],
  )

  const nodeDescription = useCallback(
    (nodeType: string, fallbackDescription?: string): string => {
      const key = canvasNodeDescriptionKey(nodeType)
      if (!i18n.exists(key, { ns: 'canvas' })) return fallbackDescription ?? ''
      return t(key)
    },
    [i18n, t],
  )

  const categoryLabel = useCallback(
    (category: CanvasNodeCategory, fallbackLabel?: string): string => {
      const key = canvasCategoryLabelKey(category)
      if (!i18n.exists(key, { ns: 'canvas' })) return fallbackLabel ?? category
      return t(key)
    },
    [i18n, t],
  )

  const nodeInstanceLabel = useCallback(
    (nodeType: string, fieldLabel: string | undefined, fallbackTypeLabel?: string): string => {
      const typeLabel = nodeLabel(nodeType, fallbackTypeLabel)
      return resolveNodeInstanceLabel(nodeType, fieldLabel, typeLabel)
    },
    [nodeLabel],
  )

  return { nodeLabel, nodeDescription, categoryLabel, nodeInstanceLabel, isSystemDefaultInstanceLabel }
}
